/**
 * Session Management Page - Epic 87
 * View and manage active login sessions
 * Carbon Design System compliant
 */

import React, { useState, useEffect } from 'react';
import styles from './SessionManagementPage.module.scss';
import {
  Content,
  Grid,
  Column,
  Heading,
  Stack,
  Button,
  Modal,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  InlineNotification,
  Loading,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import { Laptop, Phone, Tablet, Location, Time, Logout, TrashCan, Checkmark } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface SessionData {
  session_id: string;
  device_name: string | null;
  device_type: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: number;
  last_active_at: number;
  expires_at: number;
  is_current: boolean;
}

const SessionManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Modals
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [revokeAllModalOpen, setRevokeAllModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);

  const API_BASE_URL = process.env.VITE_API_BASE_URL || '/api';

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/auth/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/v1/auth/sessions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.data) {
        setSessions(response.data.data.sessions || []);
        setCurrentSessionId(response.data.data.current_session_id || null);
      }
    } catch (err: any) {
      console.error('Load sessions error:', err);

      if (err.response?.status === 401) {
        localStorage.removeItem('auth_token');
        navigate('/auth/login');
        return;
      }

      setError(err.response?.data?.message || 'Failed to load sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async () => {
    if (!selectedSession) return;

    try {
      setRevoking(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/auth/login');
        return;
      }

      await axios.delete(`${API_BASE_URL}/api/v1/auth/sessions/${selectedSession.session_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccess('Session revoked successfully');
      setRevokeModalOpen(false);
      setSelectedSession(null);

      // Reload sessions
      await loadSessions();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Revoke session error:', err);
      setError(err.response?.data?.message || 'Failed to revoke session. Please try again.');
    } finally {
      setRevoking(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    try {
      setRevoking(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/auth/login');
        return;
      }

      const response = await axios.delete(`${API_BASE_URL}/api/v1/auth/sessions/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const count = response.data.data?.revoked_count || 0;
      setSuccess(`Successfully logged out ${count} other device${count !== 1 ? 's' : ''}`);
      setRevokeAllModalOpen(false);

      // Reload sessions
      await loadSessions();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Revoke all sessions error:', err);
      setError(err.response?.data?.message || 'Failed to revoke sessions. Please try again.');
    } finally {
      setRevoking(false);
    }
  };

  const getDeviceIcon = (deviceType: string | null) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Phone size={20} />;
      case 'tablet':
        return <Tablet size={20} />;
      default:
        return <Laptop size={20} />;
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const parseDeviceName = (userAgent: string | null): string => {
    if (!userAgent) return 'Unknown Device';

    // Parse browser
    let browser = 'Unknown Browser';
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';

    // Parse OS
    let os = 'Unknown OS';
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac OS X')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    return `${browser} on ${os}`;
  };

  const headers = [
    { key: 'device', header: 'Device' },
    { key: 'location', header: 'Location' },
    { key: 'last_active', header: 'Last Active' },
    { key: 'created', header: 'Signed In' },
    { key: 'actions', header: '' },
  ];

  const rows = sessions.map(session => ({
    id: session.session_id,
    device: (
      <div className={styles.deviceInfo}>
        {getDeviceIcon(session.device_type)}
        <div>
          <div className={styles.deviceName}>{session.device_name || parseDeviceName(session.user_agent)}</div>
          {session.is_current && (
            <Tag type="green" size="sm" className={styles.currentSessionTag}>
              <Checkmark size={16} style={{ marginRight: '4px' }} />
              Current Session
            </Tag>
          )}
        </div>
      </div>
    ),
    location: (
      <div className={styles.locationInfo}>
        <Location size={16} className={styles.locationIcon} />
        <span>{session.ip_address || 'Unknown'}</span>
      </div>
    ),
    last_active: (
      <div className={styles.lastActiveInfo}>
        <Time size={16} className={styles.timeIcon} />
        <span>{formatTimestamp(session.last_active_at)}</span>
      </div>
    ),
    created: formatTimestamp(session.created_at),
    actions: !session.is_current ? (
      <Button
        kind="danger--ghost"
        size="sm"
        renderIcon={Logout}
        onClick={() => {
          setSelectedSession(session);
          setRevokeModalOpen(true);
        }}
      >
        Log Out
      </Button>
    ) : (
      <span className={styles.activeLabel}>Active</span>
    ),
  }));

  if (loading) {
    return (
      <Content>
        <div className={styles.loadingContainer}>
          <Loading description="Loading sessions..." />
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Grid>
        {/* Page Header */}
        <Column lg={16} md={8} sm={4}>
          <Stack gap={3}>
            <Heading>Active Sessions</Heading>
            <p className={styles.pageHeader}>
              Manage your active login sessions and see where your account is logged in
            </p>
          </Stack>
        </Column>

        {/* Notifications */}
        {error && (
          <Column lg={16} md={8} sm={4} className={styles.notificationMargin}>
            <InlineNotification kind="error" title="Error" subtitle={error} onCloseButtonClick={() => setError(null)} />
          </Column>
        )}

        {success && (
          <Column lg={16} md={8} sm={4} className={styles.notificationMargin}>
            <InlineNotification
              kind="success"
              title="Success"
              subtitle={success}
              onCloseButtonClick={() => setSuccess(null)}
            />
          </Column>
        )}

        {/* Log Out All Button */}
        {sessions.length > 1 && (
          <Column lg={16} md={8} sm={4} className={styles.logoutAllButton}>
            <Button kind="danger" renderIcon={TrashCan} onClick={() => setRevokeAllModalOpen(true)}>
              Log Out All Other Devices
            </Button>
          </Column>
        )}

        {/* Sessions Table */}
        <Column lg={16} md={8} sm={4} className={styles.sessionsTable}>
          {sessions.length === 0 ? (
            <InlineNotification
              kind="info"
              title="No active sessions"
              subtitle="You don't have any active sessions at the moment."
              hideCloseButton
            />
          ) : (
            <DataTable rows={rows} headers={headers}>
              {({ rows, headers, getHeaderProps, getRowProps, getTableProps, getTableContainerProps }) => (
                <TableContainer
                  title="Your Active Sessions"
                  description={`You have ${sessions.length} active session${sessions.length !== 1 ? 's' : ''}`}
                  {...getTableContainerProps()}
                >
                  <Table {...getTableProps()} aria-label="active sessions table">
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader key={header.key} {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.id} {...getRowProps({ row })}>
                          {row.cells.map(cell => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          )}
        </Column>

        {/* Security Info */}
        <Column lg={16} md={8} sm={4} className={styles.securityInfo}>
          <InlineNotification
            kind="info"
            title="Security Tip"
            subtitle="If you see any unfamiliar sessions, log them out immediately and change your password."
            lowContrast
            hideCloseButton
          />
        </Column>
      </Grid>

      {/* Revoke Single Session Modal */}
      <Modal
        open={revokeModalOpen}
        modalHeading="Log Out Session"
        modalLabel="Session Management"
        primaryButtonText={revoking ? 'Logging Out...' : 'Log Out'}
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          setRevokeModalOpen(false);
          setSelectedSession(null);
        }}
        onRequestSubmit={handleRevokeSession}
        primaryButtonDisabled={revoking}
        danger
        size="sm"
      >
        <Stack gap={4}>
          <p>Are you sure you want to log out this session? This action cannot be undone.</p>

          {selectedSession && (
            <div className={styles.sessionDetailsCard}>
              <div className={styles.detailRow}>
                <strong>Device:</strong> {selectedSession.device_name || parseDeviceName(selectedSession.user_agent)}
              </div>
              <div className={styles.detailRow}>
                <strong>Location:</strong> {selectedSession.ip_address || 'Unknown'}
              </div>
              <div className={styles.detailRow}>
                <strong>Last Active:</strong> {formatTimestamp(selectedSession.last_active_at)}
              </div>
            </div>
          )}
        </Stack>
      </Modal>

      {/* Revoke All Sessions Modal */}
      <Modal
        open={revokeAllModalOpen}
        modalHeading="Log Out All Other Devices"
        modalLabel="Session Management"
        primaryButtonText={revoking ? 'Logging Out...' : 'Log Out All Other Devices'}
        secondaryButtonText="Cancel"
        onRequestClose={() => setRevokeAllModalOpen(false)}
        onRequestSubmit={handleRevokeAllSessions}
        primaryButtonDisabled={revoking}
        danger
        size="sm"
      >
        <Stack gap={4}>
          <InlineNotification
            kind="warning"
            title="Warning"
            subtitle="This will log you out from all other devices except your current session."
            lowContrast
            hideCloseButton
          />

          <p>
            You will remain logged in on this device, but all other active sessions will be terminated. This is useful
            if you&apos;ve lost a device or suspect unauthorized access.
          </p>

          <div className={styles.revokeAllCard}>
            <strong>
              {sessions.length - 1} other session{sessions.length - 1 !== 1 ? 's' : ''} will be logged out
            </strong>
          </div>
        </Stack>
      </Modal>
    </Content>
  );
};

export default SessionManagementPage;
